import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Sheet } from './Sheet';

// Planner calendar shared by the parent's and the student's planners (#167): month (a day opens a popup),
// week (an agenda grouped by day) and day views. Item rows are rendered by the caller, so the parent gets
// edit/done/delete controls and the student a read-only row.

export interface CalendarItem {
  id: string;
  title: string;
  dueDate: string;
  done: boolean;
  /** What sort of entry this is (assignment, lesson plan, journal…); colors its chip in month view. */
  kind?: string;
  /** A time block on its day (#366), 'HH:MM' 24-hour; without one it's "any time" that day. */
  startTime?: string;
  endTime?: string;
}

/** '13:05' -> '1:05 PM' in the viewer's locale. */
const formatTime = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(2000, 0, 1, h ?? 0, m ?? 0).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};
/** '09:00', '09:45' -> '9:00 AM – 9:45 AM'; just the start when there's no end. */
export const timeRange = (start: string, end?: string) => (end ? `${formatTime(start)} – ${formatTime(end)}` : formatTime(start));

/** Something the caller can add on a day (parent only): e.g. an assignment or a lesson plan. */
export interface AddAction {
  label: string;
  onAdd: (date: string) => void;
}

export type CalendarView = 'month' | 'week' | 'day';

const pad = (n: number) => String(n).padStart(2, '0');
export const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromYmd = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
};
export const prettyDay = (s: string) =>
  s ? fromYmd(s).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : 'No due date';
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfWeek = (d: Date) => addDays(d, -d.getDay());
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const noPopup = { closePopup: () => undefined };
const VIEWS: { id: CalendarView; label: string }[] = [
  { id: 'month', label: 'Month' },
  { id: 'week', label: 'Week' },
  { id: 'day', label: 'Day' },
];

function loadView(key: string): CalendarView {
  try {
    const v = localStorage.getItem(key);
    if (v === 'month' || v === 'week' || v === 'day') return v;
  } catch {
    /* private mode etc. */
  }
  return 'month';
}

export function PlannerCalendar<T extends CalendarItem>({
  items,
  renderItem,
  actions = [],
  storageKey = 'cg.planner.view',
  focusDate,
}: {
  items: T[];
  /** One full row for an item (with whatever controls the caller wants). `closePopup` lets a row that opens
   *  its own sheet close the month view's day popup first, so sheets don't stack. */
  renderItem: (item: T, ctx: { closePopup: () => void }) => ReactNode;
  /** Parent only: things that can be added on a given day. */
  actions?: AddAction[];
  /** Remembers the chosen view per page. */
  storageKey?: string;
  /** A day ('YYYY-MM-DD') to jump to, e.g. when a link opens one assignment (#358). */
  focusDate?: string;
}) {
  const today = ymd(new Date());
  const [view, setViewState] = useState<CalendarView>(() => loadView(storageKey));
  const [cursor, setCursor] = useState(() => (focusDate ? fromYmd(focusDate) : new Date())); // the day the view is anchored on
  useEffect(() => {
    if (focusDate) setCursor(fromYmd(focusDate));
  }, [focusDate]);
  const [popup, setPopup] = useState<string | null>(null); // month view: the day whose popup is open

  const setView = (v: CalendarView) => {
    setViewState(v);
    try {
      localStorage.setItem(storageKey, v);
    } catch {
      /* ignore */
    }
  };

  const byDay = new Map<string, T[]>();
  for (const it of items) if (it.dueDate) (byDay.get(it.dueDate) ?? byDay.set(it.dueDate, []).get(it.dueDate)!).push(it);
  // Within a day, scheduled items come first in time order (#366); the rest keep their order after them.
  for (const list of byDay.values()) {
    list.sort((a, b) => (a.startTime ? (b.startTime ? a.startTime.localeCompare(b.startTime) : -1) : b.startTime ? 1 : 0));
  }
  const itemsOn = (day: string) => byDay.get(day) ?? [];

  // Navigation and the label depend on the view.
  const shift = (n: number) =>
    setCursor((c) => (view === 'month' ? new Date(c.getFullYear(), c.getMonth() + n, 1) : addDays(c, view === 'week' ? 7 * n : n)));
  const weekStart = startOfWeek(cursor);
  const label =
    view === 'month'
      ? cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      : view === 'week'
        ? `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${addDays(weekStart, 6).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
        : prettyDay(ymd(cursor));
  const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';

  const dayList = (day: string, heading: boolean) => {
    const list = itemsOn(day);
    return (
      <div key={day} className={`stack cal-agenda-day${day === today ? ' today' : ''}`}>
        {heading && (
          <div className="row-between">
            <h3>{prettyDay(day)}</h3>
            <AddButtons actions={actions} date={day} />
          </div>
        )}
        {list.length === 0 ? <p className="muted small">Nothing due.</p> : <TimeBlocks list={list} render={(it) => renderItem(it, noPopup)} />}
      </div>
    );
  };

  return (
    <div className="stack">
      <div className="cal-toolbar">
        <div className="segmented" role="radiogroup" aria-label="Calendar view">
          {VIEWS.map((v) => (
            <button key={v.id} role="radio" aria-checked={view === v.id} className={view === v.id ? 'active' : ''} onClick={() => setView(v.id)}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="cal-head">
          <button className="icon-btn" onClick={() => shift(-1)} aria-label={`Previous ${unit}`}>
            <ChevronLeft size={18} />
          </button>
          <strong>{label}</strong>
          <button className="icon-btn" onClick={() => shift(1)} aria-label={`Next ${unit}`}>
            <ChevronRight size={18} />
          </button>
        </div>
        <button className="btn small" onClick={() => setCursor(new Date())}>
          Today
        </button>
      </div>

      {view === 'month' && (
        <div className="cal-grid">
          {WEEKDAYS.map((w) => (
            <div key={w} className="cal-weekday">
              {w}
            </div>
          ))}
          {Array.from({ length: 42 }, (_, i) => addDays(startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1)), i)).map((d) => {
            const key = ymd(d);
            const list = itemsOn(key);
            const inMonth = d.getMonth() === cursor.getMonth();
            const allDone = list.length > 0 && list.every((i) => i.done);
            return (
              <button
                key={key}
                className={`cal-cell${inMonth ? '' : ' other-month'}${key === today ? ' today' : ''}`}
                onClick={() => setPopup(key)}
                aria-label={`${prettyDay(key)}: ${list.length ? `${list.length} due` : 'nothing due'}`}
              >
                <span className="cal-daynum">{d.getDate()}</span>
                {list.length > 0 && (
                  <span className="cal-chips">
                    {list.slice(0, 2).map((it) => (
                      <span key={it.id} className={`cal-chip${it.done ? ' done' : ''}${it.kind ? ` kind-${it.kind}` : ''}`}>
                        {it.startTime && <span className="cal-chip-time">{formatTime(it.startTime)} </span>}
                        {it.title}
                      </span>
                    ))}
                    {list.length > 2 && <span className="cal-more">+{list.length - 2} more</span>}
                  </span>
                )}
                {list.length > 0 && <span className={`cal-dot${allDone ? ' done' : ''}`}>{list.length}</span>}
              </button>
            );
          })}
        </div>
      )}

      {view === 'week' && <div className="stack cal-agenda">{Array.from({ length: 7 }, (_, i) => dayList(ymd(addDays(weekStart, i)), true))}</div>}

      {view === 'day' && (
        <div className="stack">
          {actions.length > 0 && (
            <div className="row-between">
              <span />
              <AddButtons actions={actions} date={ymd(cursor)} />
            </div>
          )}
          {dayList(ymd(cursor), false)}
        </div>
      )}

      {popup && (
        <Sheet title={prettyDay(popup)} onClose={() => setPopup(null)}>
          <div className="stack">
            {itemsOn(popup).length === 0 ? (
              <p className="muted">Nothing due this day.</p>
            ) : (
              <TimeBlocks list={itemsOn(popup)} render={(it) => renderItem(it, { closePopup: () => setPopup(null) })} />
            )}
            {actions.length > 0 && (
              <div className="cal-popup-actions">
                {actions.map((a) => (
                  <button
                    key={a.label}
                    className="btn"
                    onClick={() => {
                      const day = popup;
                      setPopup(null);
                      a.onAdd(day);
                    }}
                  >
                    <Plus size={16} /> {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Sheet>
      )}
    </div>
  );
}

/**
 * A day's items as time blocks (#366): scheduled ones (already first, in time order) each with their time beside
 * them, then an "Any time" group for the rest. A day with nothing scheduled is just the plain list.
 */
function TimeBlocks<T extends CalendarItem>({ list, render }: { list: T[]; render: (item: T) => ReactNode }) {
  const timed = list.filter((it) => it.startTime);
  const anytime = list.filter((it) => !it.startTime);
  if (!timed.length) return <>{list.map((it) => <div key={it.id}>{render(it)}</div>)}</>;
  return (
    <>
      {timed.map((it) => (
        <div key={it.id} className="time-block">
          <span className="time-block-time">
            {formatTime(it.startTime!)}
            {it.endTime && <span className="time-block-end">to {formatTime(it.endTime)}</span>}
          </span>
          <div className="time-block-item">{render(it)}</div>
        </div>
      ))}
      {anytime.length > 0 && (
        <>
          <div className="time-block-heading muted small">Any time that day</div>
          {anytime.map((it) => (
            <div key={it.id}>{render(it)}</div>
          ))}
        </>
      )}
    </>
  );
}

function AddButtons({ actions, date }: { actions: AddAction[]; date: string }) {
  if (!actions.length) return null;
  return (
    <span className="row-gap cal-add">
      {actions.map((a) => (
        <button key={a.label} className="btn small" onClick={() => a.onAdd(date)} aria-label={`${a.label} on ${prettyDay(date)}`}>
          <Plus size={14} /> {a.label}
        </button>
      ))}
    </span>
  );
}
